import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useParams, useLocation } from 'react-router-dom';
import { ServiceType, getCityFromSlug, getLocalizedContent, services } from '../routes/seoRoutes';

export const Faq: React.FC = () => {
  const { city: urlCitySlug } = useParams<{ city: string }>();
  const location = useLocation();
  
  // Determine service from URL path
  const determineServiceFromPath = (pathname: string): ServiceType | undefined => {
    const sortedServices = Object.entries(services).sort(([,a], [,b]) => b.path.length - a.path.length);
    
    for (const [serviceKey, serviceConfig] of sortedServices) {
      const pattern = `/${serviceConfig.path}-`;
      if (pathname.startsWith(pattern)) {
        return serviceKey as ServiceType;
      }
    }
    return undefined;
  };

  const service = determineServiceFromPath(location.pathname);
  const cityName = urlCitySlug ? getCityFromSlug(urlCitySlug) : undefined;
  const localizedContent = getLocalizedContent(cityName, service);
  
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const scrollToForm = () => {
    const formSection = document.getElementById('quote-section');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // FAQ localizzate per l'Italia
  const faqs = [
    {
      question: `Quanto tempo ci vorrà per il ritiro della mia auto${cityName ? ` a ${cityName}` : ''}?`,
      answer: `Procederemo con il ritiro dell'auto non appena accetterai la nostra offerta. Il nostro operatore sarà ${cityName ? `a ${cityName}` : 'da te'} all'orario stabilito. Per far ritirare la tua auto entro 24 ore, compila subito il modulo di preventivo online.`
    },
    {
      question: `Come ottenere una valutazione rapida per la mia auto${cityName ? ` a ${cityName}` : ''}?`,
      answer: `Offriamo valutazioni rapide per auto ${cityName ? `a ${cityName} e in tutto il Nord Italia` : 'in tutto il Nord Italia'}. Se hai deciso di vendere il tuo veicolo per un buon prezzo, compila il nostro modulo di preventivo online o chiamaci. Ti risponderemo con un'offerta personalizzata. Una volta accettata l'offerta, il nostro team organizzerà il ritiro e il pagamento.`
    },
    {
      question: "Come decidete il preventivo per la mia auto?",
      answer: `Ci impegniamo a fornirti un preventivo accurato e offriamo prezzi competitivi sul mercato italiano. L'offerta può essere influenzata da caratteristiche come condizioni, età, chilometraggio, modello e anno. Consideriamo anche i valori di mercato locali${cityName ? ` specifici per la zona di ${cityName}` : ''}.`
    },
    {
      question: "Accettate auto danneggiate o incidentate?",
      answer: `Sì, accettiamo tutti i tipi di auto in qualsiasi condizione. Se il tuo veicolo è stato coinvolto in un incidente, non preoccuparti. Puoi venderlo a noi per ottenere il massimo valore possibile${cityName ? ` nella zona di ${cityName}` : ''}.`
    },
    {
      question: `È sicuro vendere la mia auto${cityName ? ` a ${cityName}` : ''}?`,
      answer: `Assolutamente! È totalmente sicuro vendere la tua auto con noi. Operiamo ${cityName ? `a ${cityName} e` : ''} in tutto il Nord Italia da molti anni, quindi puoi fidarti di noi per il processo di vendita dell'auto. Se hai ancora dei dubbi, il nostro team è disponibile per rispondere alle tue domande.`
    },
    {
      question: "Acquistate auto con fermi amministrativi?",
      answer: `Sì, garantiamo l'acquisto, il ritiro e il pagamento immediato anche per auto con fermi amministrativi. Gestiamo tutta la documentazione necessaria con la massima professionalità e trasparenza${cityName ? ` anche a ${cityName}` : ''}.`
    },
    {
      question: "Quali tipi di veicoli acquistate?",
      answer: `Acquistiamo tutti i tipi di veicoli: auto, camion, furgoni, SUV, 4x4 e veicoli commerciali. Che il tuo veicolo sia danneggiato, vecchio, non funzionante o in perfette condizioni, ti faremo un'offerta equa${cityName ? ` a ${cityName}` : ''}.`
    }
  ];

  return (
    <section className="relative py-20 overflow-hidden">
      {/* Background Overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(/img/overlay2.png)',
          opacity: 0.1
        }}
      />

      <div className="container relative z-10 px-4 mx-auto">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          {/* Left Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <img
              src="/img/faq.jpg"
              alt={`FAQ ValutaAuto ${cityName || ''}`}
              className="object-cover w-full rounded-lg shadow-xl"
            />
            {/* Contact Overlay */}
            <div
              className="absolute bottom-0 left-0 right-0 p-6 text-white"
              style={{
                backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(/img/assistenza_telefonica.png)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              <p className="mb-2 text-2xl font-bold">Hai bisogno di aiuto?</p>
              <p className="mb-4">
                {cityName 
                  ? `Il nostro team è qui per assisterti a ${cityName} 24 ore su 24, 7 giorni su 7`
                  : 'Il nostro team è qui per assisterti 24 ore su 24, 7 giorni su 7'
                }
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToForm}
                className="inline-flex items-center px-6 py-3 bg-[#fdc751] text-gray-900 rounded-lg font-semibold hover:bg-[#fdc751]/90 transition"
              >
                {cityName ? `Richiedi valutazione a ${cityName}` : 'Richiedi valutazione ora'}
              </motion.button>
            </div>
          </motion.div>

          {/* Right Column - FAQs */}
          <div className="space-y-6">
            <div className="mb-8">
              <h2 className="mb-4 text-3xl font-bold">{localizedContent.faq.title}</h2>
              <p className="text-gray-600">
                {localizedContent.faq.description}
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="overflow-hidden bg-white rounded-lg shadow-md"
                >
                  <button
                    onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                    className="flex items-center justify-between w-full p-4 text-left hover:bg-gray-50"
                  >
                    <span className="font-medium">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${
                        activeIndex === index ? 'transform rotate-180 text-[#fdc751]' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 pb-4"
                      >
                        <p className="text-gray-600">{faq.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


